import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PayoutConfigEditor } from '../../components';
import type { Board, NumberMode } from '../../types';
import { getNumberSetKeys } from '../../types';

export interface RulesPanelProps {
  board: Board;
}

// Mini grid component for visual examples
const MiniGrid: React.FC<{
  homeDigit: number;
  awayDigit: number;
  highlightCell?: { row: number; col: number };
  highlightColor?: string;
  secondaryCell?: { row: number; col: number };
  secondaryColor?: string;
  label?: string;
}> = ({ homeDigit, awayDigit, highlightCell, highlightColor = '#4caf50', secondaryCell, secondaryColor = '#ff9800', label }) => {
  const gridSize = 5; // 5x5 mini grid for simplicity
  const cellSize = 28;

  return (
    <Box sx={{ display: 'inline-block', textAlign: 'center' }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box sx={{ display: 'inline-block', border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
        {/* Column headers */}
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ width: cellSize, height: cellSize, bgcolor: '#f5f5f5' }} />
          {Array.from({ length: gridSize }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: cellSize,
                height: cellSize,
                bgcolor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderLeft: '1px solid #e0e0e0',
                fontWeight: i === homeDigit % gridSize ? 'bold' : 'normal',
                color: i === homeDigit % gridSize ? 'primary.main' : 'text.secondary',
              }}
            >
              <Typography variant="caption">{i}</Typography>
            </Box>
          ))}
        </Box>
        {/* Grid rows */}
        {Array.from({ length: gridSize }, (_, row) => (
          <Box key={row} sx={{ display: 'flex' }}>
            {/* Row header */}
            <Box
              sx={{
                width: cellSize,
                height: cellSize,
                bgcolor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderTop: '1px solid #e0e0e0',
                fontWeight: row === awayDigit % gridSize ? 'bold' : 'normal',
                color: row === awayDigit % gridSize ? 'primary.main' : 'text.secondary',
              }}
            >
              <Typography variant="caption">{row}</Typography>
            </Box>
            {/* Cells */}
            {Array.from({ length: gridSize }, (_, col) => {
              const isHighlighted = highlightCell && highlightCell.row === row && highlightCell.col === col;
              const isSecondary = secondaryCell && secondaryCell.row === row && secondaryCell.col === col;
              return (
                <Box
                  key={col}
                  sx={{
                    width: cellSize,
                    height: cellSize,
                    bgcolor: isHighlighted ? highlightColor : isSecondary ? secondaryColor : '#fff',
                    borderTop: '1px solid #e0e0e0',
                    borderLeft: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {(isHighlighted || isSecondary) && (
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.65rem' }}>
                      {isHighlighted ? '✓' : '↔'}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const RulesPanel: React.FC<RulesPanelProps> = ({ board }) => {
  // Check if Wrong Way rule is enabled
  const hasWrongWayPayouts = board.payoutConfig.quarterWrongWayPayouts &&
    Object.values(board.payoutConfig.quarterWrongWayPayouts).some(v => v > 0);

  // Check if +N rule is enabled
  const hasPlusNPayouts = board.payoutConfig.quarterPlusNPayouts &&
    Object.values(board.payoutConfig.quarterPlusNPayouts).some(v => v > 0);
  const plusNOffset = board.payoutConfig.plusNOffset ?? 5;

  // Number mode settings
  const numberMode: NumberMode = board.numberMode || 'single';
  const numberSetKeys = getNumberSetKeys(board.quarters || 4, numberMode);
  const hasMultipleNumberSets = numberSetKeys.length > 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Game Rules
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Game Basics
        </Typography>
        <Typography variant="body2" paragraph>
          This is a grid of squares. Each square costs <strong>${board.pricePerSquare.toFixed(2)}</strong>.
          Players claim squares before the game, and winners are determined by the last digit of each team's score
          at the end of each quarter.
        </Typography>

        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
          How Squares Work
        </Typography>
        <Typography variant="body2" paragraph>
          • Each row corresponds to the {board.homeOnTop ? board.awayTeam : board.homeTeam} ({board.homeOnTop ? 'Away' : 'Home'}) score's last digit (0-9)
        </Typography>
        <Typography variant="body2" paragraph>
          • Each column corresponds to the {board.homeOnTop ? board.homeTeam : board.awayTeam} ({board.homeOnTop ? 'Home' : 'Away'}) score's last digit (0-9)
        </Typography>
        <Typography variant="body2" paragraph>
          • The numbers shown on the grid edges are randomized after all squares are claimed - they're hidden until then
        </Typography>
        <Typography variant="body2" paragraph>
          • Example: If the score is {board.homeOnTop ? board.homeTeam : board.awayTeam} 24 - {board.homeOnTop ? board.awayTeam : board.homeTeam} 17,
          the winning square is at column 4 (home last digit) and row 7 (away last digit)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Link component={RouterLink} to="/support/game-mechanics#the-grid">
            Learn more about how the grid works →
          </Link>
        </Typography>
      </Paper>

      {/* Multi-Number Mode Explanation */}
      {hasMultipleNumberSets && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50', borderColor: 'primary.main', border: '1px solid' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
              Multiple Number Sets
            </Typography>
            <Chip
              label={numberMode === 'half' ? 'Half Mode' : 'Quarter Mode'}
              size="small"
              color="primary"
            />
          </Box>

          <Typography variant="body2" paragraph>
            Unlike traditional boards that use one set of numbers for the entire game, this board uses{' '}
            <strong>different numbers for different periods</strong>. This means you have more chances to win!
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            How It Works
          </Typography>
          <Typography variant="body2" paragraph>
            • The grid shows {numberSetKeys.length} rows of numbers at the top and {numberSetKeys.length} columns on the left
          </Typography>
          <Typography variant="body2" paragraph>
            • Each row/column is labeled ({numberSetKeys.join(', ')}) to show which period it applies to
          </Typography>
          <Typography variant="body2" paragraph>
            • When a score is recorded, use the numbers for that specific period to find the winning square
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Which Numbers Apply When?
          </Typography>
          <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1, mb: 2 }}>
            {numberMode === 'half' && board.quarters === 4 && (
              <>
                <Typography variant="body2">• <strong>Q1 & Q2:</strong> Use the <Chip label="1H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>Q3 & Q4:</strong> Use the <Chip label="2H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
            {numberMode === 'half' && board.quarters === 3 && (
              <>
                <Typography variant="body2">• <strong>Period 1:</strong> Use the <Chip label="1H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>Periods 2 & 3:</strong> Use the <Chip label="2H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
            {numberMode === 'half' && board.quarters === 2 && (
              <>
                <Typography variant="body2">• <strong>1st Half:</strong> Use the <Chip label="1H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>2nd Half:</strong> Use the <Chip label="2H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
            {numberMode === 'quarter' && board.quarters === 4 && (
              <>
                <Typography variant="body2">• <strong>Q1:</strong> Use <Chip label="Q1" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers &nbsp; • <strong>Q2:</strong> Use <Chip label="Q2" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>Q3:</strong> Use <Chip label="Q3" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers &nbsp; • <strong>Q4:</strong> Use <Chip label="Q4" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
            {numberMode === 'quarter' && board.quarters === 3 && (
              <>
                <Typography variant="body2">• <strong>P1:</strong> Use <Chip label="P1" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>P2:</strong> Use <Chip label="P2" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>P3:</strong> Use <Chip label="P3" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
            {numberMode === 'quarter' && board.quarters === 2 && (
              <>
                <Typography variant="body2">• <strong>1st Half:</strong> Use the <Chip label="1H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
                <Typography variant="body2">• <strong>2nd Half:</strong> Use the <Chip label="2H" size="small" sx={{ height: 18, fontSize: '0.7rem' }} /> numbers</Typography>
              </>
            )}
          </Box>

          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 1 }}>
            Tip: When you hover over a recorded score, the grid will highlight which number row/column applies.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/support/game-mechanics#multi-number-sets">
              Learn more about multi-number sets →
            </Link>
          </Typography>
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Payout Structure
        </Typography>
        <PayoutConfigEditor
          payoutConfig={board.payoutConfig}
          onPayoutConfigChange={() => {}}
          pricePerSquare={board.pricePerSquare}
          profitPercent={board.payoutConfig.profitPercent ?? 10}
          onProfitPercentChange={() => {}}
          quarters={board.quarters || 4}
          readOnly
          hideProfit
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          <Link component={RouterLink} to="/support/payouts#quarter-payouts">
            Learn more about payouts →
          </Link>
        </Typography>
      </Paper>

      {/* Wrong Way Rule Explanation */}
      {hasWrongWayPayouts && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={{ fontSize: '1.2rem' }}>↔️</Box>
            Wrong Way (Reverse) Rule
          </Typography>
          <Typography variant="body2" paragraph>
            In addition to the regular winner, the <strong>Wrong Way</strong> rule pays a bonus to the square
            whose numbers match the score <em>in reverse</em> — where the home and away digits are swapped.
          </Typography>

          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', my: 2, justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Score: Home 14 - Away 21
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <MiniGrid
                  homeDigit={4}
                  awayDigit={1}
                  highlightCell={{ row: 1, col: 4 }}
                  highlightColor="#4caf50"
                  label="Regular Winner (4,1)"
                />
                <MiniGrid
                  homeDigit={1}
                  awayDigit={4}
                  highlightCell={{ row: 4, col: 1 }}
                  highlightColor="#ff9800"
                  label="Wrong Way (1,4)"
                />
              </Box>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Example:</strong> If the score ends Home 14 - Away 21, the regular winner has digits (4, 1).
            The Wrong Way winner has digits (1, 4) — the reverse. Both squares pay out!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/support/payouts#reverse-payouts">
              Learn more about wrong way payouts →
            </Link>
          </Typography>
        </Paper>
      )}

      {/* +N Rule Explanation */}
      {hasPlusNPayouts && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={{ fontSize: '1.2rem' }}>➕</Box>
            +{plusNOffset} Rule
          </Typography>
          <Typography variant="body2" paragraph>
            The <strong>+{plusNOffset} Rule</strong> pays a bonus to a second square by adding {plusNOffset} to each
            score digit. This creates an additional winner every quarter!
          </Typography>

          <Box sx={{ my: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>How it works:</strong> Take each score digit, add {plusNOffset}, and wrap around at 10.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {Array.from({ length: 10 }, (_, i) => (
                <Box
                  key={i}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    bgcolor: 'info.100',
                    borderRadius: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {i} → {(i + plusNOffset) % 10}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', my: 2, justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Score: Home 21 - Away 10 (digits 1, 0)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <MiniGrid
                  homeDigit={1}
                  awayDigit={0}
                  highlightCell={{ row: 0, col: 1 }}
                  highlightColor="#4caf50"
                  label="Regular Winner (1,0)"
                />
                <MiniGrid
                  homeDigit={(1 + plusNOffset) % 10}
                  awayDigit={(0 + plusNOffset) % 10}
                  highlightCell={{ row: (0 + plusNOffset) % 5, col: (1 + plusNOffset) % 5 }}
                  highlightColor="#2196f3"
                  label={`+${plusNOffset} Winner (${(1 + plusNOffset) % 10},${(0 + plusNOffset) % 10})`}
                />
              </Box>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Example:</strong> If the score ends Home 21 - Away 10, the regular winner has digits (1, 0).
            Adding {plusNOffset} to each: 1+{plusNOffset}={(1 + plusNOffset) % 10}, 0+{plusNOffset}={(0 + plusNOffset) % 10}.
            So the +{plusNOffset} rule also pays out square ({(1 + plusNOffset) % 10}, {(0 + plusNOffset) % 10})!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/support/payouts#plus-n-rule">
              Learn more about the +{plusNOffset} rule →
            </Link>
          </Typography>
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Square Colors
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 24, height: 24, bgcolor: '#ffffff', border: '1px solid #ccc', borderRadius: 0.5 }} />
            <Typography variant="body2">Available</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 24, height: 24, bgcolor: '#2196f3', borderRadius: 0.5 }} />
            <Typography variant="body2">Your Squares</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 24, height: 24, bgcolor: '#ff9800', borderRadius: 0.5 }} />
            <Typography variant="body2">Claimed by Others</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 24, height: 24, bgcolor: '#4caf50', borderRadius: 0.5 }} />
            <Typography variant="body2">Winner</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Tips
        </Typography>
        <Typography variant="body2" paragraph>
          • Numbers 0, 3, and 7 tend to be more common ending digits in football scores
        </Typography>
        <Typography variant="body2" paragraph>
          • In basketball, any digit can be equally likely since scores are higher
        </Typography>
        <Typography variant="body2" paragraph>
          • The randomization ensures fair distribution of number assignments
        </Typography>
        <Typography variant="body2" paragraph>
          • Share the invite code <strong>{board.inviteCode}</strong> with friends to let them join!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Link component={RouterLink} to="/support/game-mechanics#probabilities">
            Learn more about number probabilities →
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};
